import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useDuckDB } from '../../../../contexts/DuckDBContext';

export const useDuckDBIntegration = (open) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // DuckDB integration
  const { 
    isInitialized, 
    isLoading: duckDBLoading, 
    error: duckDBError,
    testConnection,
    executeQuery,
    registerFile,
    connection 
  } = useDuckDB();

  // DuckDB test results
  const [duckDBStatus, setDuckDBStatus] = useState('initializing');
  const [duckDBLogs, setDuckDBLogs] = useState([]);
  const [testQueryResult, setTestQueryResult] = useState(null);

  // Add log helper
  const addLog = (message) => {
    console.log(`[DuckDB] ${message}`);
    setDuckDBLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message
    }].slice(-5)); 
  };

  // Test DuckDB connection when modal opens
  const testDuckDBConnection = useCallback(async () => {
    if (!isInitialized || !connection) {
      addLog('⚠️ DuckDB not initialized yet');
      return;
    }

    console.log("isInitailized : ", isInitialized);
    console.log("connection :", connection);

    try {
      addLog('🔍 Testing DuckDB connection...');
      
      setDuckDBStatus('ready');
      addLog('🎉 DuckDB is fully operational!');
      
    } catch (error) {
      console.error('DuckDB test failed:', error);
      setDuckDBStatus('error');
      addLog(`❌ Test failed: ${error.message}`);
      enqueueSnackbar('DuckDB test failed', { variant: 'error' });
    }
  }, [isInitialized, connection, executeQuery, enqueueSnackbar]);

  // Initialize DuckDB test when modal opens
  useEffect(() => {
    if (open && isInitialized) {
      testDuckDBConnection();
    }
  }, [open, isInitialized, testDuckDBConnection]);

  // Profile source data using DuckDB
  const profileSourceData = useCallback(async (tableName, csvData) => {
    if (!isInitialized || !connection) return null;

    try {
      addLog(`📊 Profiling data for ${tableName}...`);
      
      // Register the CSV file
      const fileName = `${tableName}_${Date.now()}.csv`;
      await registerFile(fileName, csvData);
      
      // Create table from CSV
      await executeQuery(`
        CREATE OR REPLACE TEMP TABLE ${tableName} AS
        SELECT * FROM read_csv_auto('${fileName}')
      `);
      
      // Get column information
      const columns = await executeQuery(`
        SELECT 
          column_name,
          data_type
        FROM information_schema.columns 
        WHERE table_name = '${tableName}'
      `);
      
      addLog(`✅ Found ${columns.length} columns in ${tableName}`);

      const profiles = await columns.reduce(
        async (accPromise, col) => {
          const acc = await accPromise;
          const profile = await executeQuery(`
          SELECT 
            COUNT(*) as total_rows,
            COUNT(DISTINCT ${col.column_name}) as distinct_count,
            SUM(CASE WHEN ${col.column_name} IS NULL THEN 1 ELSE 0 END) as null_count,
            MIN(${col.column_name}) as min_value,
            MAX(${col.column_name}) as max_value
          FROM ${tableName}
        `);
          return { ...acc, [col.column_name]: profile[0] };
        },
        Promise.resolve({})
      );
      
      addLog(`✅ Data profiling completed for ${tableName}`);
      return { columns, profiles };
      
    } catch (error) {
      addLog(`❌ Profiling failed: ${error.message}`);
      console.error('Profiling error:', error);
      return null;
    }
  }, [isInitialized, connection, executeQuery, registerFile]);

  return {
    isInitialized,
    duckDBLoading,
    duckDBError,
    duckDBStatus,
    duckDBLogs,
    testQueryResult,
    addLog,
    testDuckDBConnection,
    profileSourceData,
    connection,
    executeQuery,
    registerFile
  };
};